/**
 * TanStack Query hooks for `advertiser-approval`.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	approve,
	reject,
	fetchQueue,
	fetchAudit,
	advertiserApprovalQueryKeys,
	type AuditResponse,
	type DecisionResponse,
	type QueueParams,
	type QueueResponse,
} from "./api";

export function useAdvertiserApprovalQueue(
	params: QueueParams,
): UseQueryResult<QueueResponse, Error> {
	return useQuery({
		queryKey: advertiserApprovalQueryKeys.queue(
			params.advertiserId,
			params.status ?? "pending",
		),
		queryFn: () => fetchQueue(params),
		enabled: params.advertiserId.trim().length > 0,
	});
}

export function useAdvertiserApprovalAudit(
	advertiserId: string,
): UseQueryResult<AuditResponse, Error> {
	return useQuery({
		queryKey: advertiserApprovalQueryKeys.audit(advertiserId),
		queryFn: () => fetchAudit({ advertiserId }),
		enabled: advertiserId.length > 0,
	});
}

export function useAdvertiserApprovalApprove(): UseMutationResult<
	DecisionResponse,
	Error,
	{ id: string }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id }) => approve(id),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: advertiserApprovalQueryKeys.all });
		},
	});
}

export function useAdvertiserApprovalReject(): UseMutationResult<
	DecisionResponse,
	Error,
	{ id: string; reason: string }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, reason }) => reject(id, reason),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: advertiserApprovalQueryKeys.all });
		},
	});
}
